// @flow
import React, { Component, Fragment } from 'react';
import classnames from 'classnames';

import ReactAppContext from 'Context/ReactAppContext';
import {
  hasAtLeastOnePrice,
  hasPrice,
  getInstalmentPrice,
} from 'Components/utils/price/price.util';
import { ICONS } from 'Components/utils/icon/icons';

import { PriceItem } from 'Components/frameworks/price-item/PriceItem.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { getKeyValue } from 'Src/i18n';

import { themes } from './Price.themes';

export type PriceProps = {
  bestPrice?: FormattedPrice,
  className?: string,
  from?: string,
  hasInitialPrice?: boolean,
  initialPrice?: FormattedPrice,
  instead?: string,
  isLoading?: boolean,
  style?: *,
  containerStyle?: *,
  theme?: string,
  unit?: string,
  viewRates?: string,
  onClick?: (e: SyntheticEvent<*>) => void,
  isValueClickable?: boolean,
  withAsterisk?: boolean,
  withInstalment?: boolean,
};

export class Price extends Component<PriceProps> {
  static contextType = ReactAppContext;

  static defaultProps = {
    containerStyle: { minHeight: '2.5rem' },
    hasInitialPrice: true,
    isLoading: false,
    theme: 'package',
    isValueClickable: false,
    withAsterisk: true,
    withInstalment: true,
  };

  hasDiscount(): boolean {
    const { bestPrice = {}, initialPrice = {} } = this.props;
    return hasPrice(initialPrice) && hasPrice(bestPrice) && initialPrice.value !== bestPrice.value;
  }

  renderViewRate() {
    const { theme, viewRates } = this.props;

    return (
      <div className={themes[theme].viewRates}>
        <Icon className={themes[theme].viewRatesIcon} name={ICONS.DATE} width="1em" />
        <span className={themes[theme].viewRatesLabel} data-testid="Price-viewRate">
          {viewRates}
        </span>
      </div>
    );
  }

  renderUnit() {
    const { theme, unit, withAsterisk } = this.props;

    return unit ? (
      <span className={themes[theme].unit} data-testid="Price-unit">
        {unit}
        {withAsterisk && <sup>*</sup>}
      </span>
    ) : null;
  }

  renderNoDiscount() {
    const { bestPrice, from, initialPrice, theme, isValueClickable, withInstalment } = this.props;

    let price;
    if (hasPrice(initialPrice)) {
      price = initialPrice;
    } else if (hasPrice(bestPrice)) {
      price = bestPrice;
    }

    return (
      <Fragment>
        <PriceItem
          label={from}
          theme={theme}
          type="best"
          {...price}
          formattedValue={
            withInstalment
              ? getInstalmentPrice(price, this.context.locale)
              : price && price.formattedValue
          }
          className={themes[theme].price}
          isValueClickable={isValueClickable}
        />
        <span> </span>
        {this.renderUnit()}
      </Fragment>
    );
  }

  renderDiscount() {
    const {
      bestPrice,
      from,
      hasInitialPrice,
      initialPrice = {},
      instead,
      theme,
      isValueClickable,
      withInstalment,
    } = this.props;

    const displayInstalment = withInstalment && getKeyValue('common.price.displayInstalmentsPrice');

    const { locale } = this.context;

    return (
      <Fragment>
        <PriceItem
          label={from}
          theme={theme}
          type="best"
          {...bestPrice}
          formattedValue={
            withInstalment
              ? getInstalmentPrice(bestPrice, locale)
              : bestPrice && bestPrice.formattedValue
          }
          className={themes[theme].price}
          isValueClickable={isValueClickable}
        />
        <span> </span>
        {hasInitialPrice && (
          <PriceItem
            label={instead}
            theme={theme}
            type="initial"
            {...initialPrice}
            formattedValue={
              displayInstalment && initialPrice && ['pt-BR', 'es-AR'].includes(locale)
                ? formatAmountForLocale(
                    Math.trunc(initialPrice.value),
                    locale,
                    initialPrice.currency,
                    {
                      precision: 0,
                    },
                  )
                : initialPrice && initialPrice.formattedValue
            }
            className={themes[theme].initialPrice}
          />
        )}
        <span> </span>
        {this.renderUnit()}
      </Fragment>
    );
  }

  renderPrice() {
    return this.hasDiscount() ? this.renderDiscount() : this.renderNoDiscount();
  }

  renderContent(displayPrice: boolean) {
    const { isLoading } = this.props;

    if (isLoading) {
      return <div className="loadingDots" data-testid="Price-loader" />;
    }

    if (displayPrice) {
      return this.renderPrice();
    }

    return this.renderViewRate();
  }

  render() {
    const displayPrice = hasAtLeastOnePrice(this.props);

    if (!displayPrice && !this.props.viewRates) {
      return null;
    }

    const { className, containerStyle, style, theme, onClick } = this.props;

    const classes = classnames(className, themes[theme].root, { 'reset-button': !!onClick });

    const TagName = onClick ? 'button' : 'div';

    return (
      <TagName className={classes} style={containerStyle} onClick={onClick}>
        <div className={themes[theme].priceRoot} style={style}>
          {this.renderContent(displayPrice)}
        </div>
      </TagName>
    );
  }
}
